import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Settings, Shield, Database, Server, 
  CheckCircle, XCircle, RefreshCw, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettings() {
  const { api, user } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchHealth();
  }, []);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/system/health');
      setHealth(response.data);
    } catch (error) {
      console.error('Error fetching health:', error);
      toast.error('Erro ao verificar status do sistema');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchHealth();
    setRefreshing(false);
    toast.success('Status atualizado');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Configurações</h1>
        <p className="text-slate-400">Configurações do sistema e informações do admin</p>
      </div>

      {/* Admin Info */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-400" />
            Informações do Admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-400 mb-1">Nome</p>
              <p className="text-white font-medium">{user?.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Email</p>
              <p className="text-white font-medium">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Role</p>
              <Badge className="bg-orange-500/20 text-orange-400">
                <Shield className="w-3 h-3 mr-1" />
                Admin Master
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">ID</p>
              <p className="text-white font-mono text-sm">{user?.id}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Health */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-400" />
                Status do Sistema
              </CardTitle>
              <CardDescription className="text-slate-400">
                Monitoramento em tempo real dos serviços
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-700 text-slate-300"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : health ? (
            <div className="space-y-4">
              {/* Overall Status */}
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {health.status === 'healthy' ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-400" />
                  )}
                  <div>
                    <p className="font-medium text-white">Status Geral</p>
                    <p className="text-sm text-slate-400">Sistema operacional</p>
                  </div>
                </div>
                <Badge className={
                  health.status === 'healthy' 
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                }>
                  {health.status === 'healthy' ? 'Saudável' : 'Degradado'}
                </Badge>
              </div>

              {/* Database Status */}
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Database className={`w-6 h-6 ${health.database === 'healthy' ? 'text-green-400' : 'text-red-400'}`} />
                  <div>
                    <p className="font-medium text-white">MongoDB</p>
                    <p className="text-sm text-slate-400">Banco de dados</p>
                  </div>
                </div>
                <Badge className={
                  health.database === 'healthy' 
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                }>
                  {health.database === 'healthy' ? 'Conectado' : 'Erro'}
                </Badge>
              </div>

              {/* Timestamp */}
              <p className="text-xs text-slate-500 text-right">
                Última verificação: {health.timestamp ? new Date(health.timestamp).toLocaleString('pt-BR') : '-'}
              </p>
            </div>
          ) : (
            <p className="text-slate-400">Não foi possível obter status do sistema</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-purple-400" />
            Ações Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 border-slate-700 text-slate-300 hover:bg-slate-700"
              onClick={() => window.location.href = '/admin/users'}
            >
              <Shield className="w-6 h-6 text-blue-400" />
              <span>Gerenciar Usuários</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 border-slate-700 text-slate-300 hover:bg-slate-700"
              onClick={() => window.location.href = '/admin/trips'}
            >
              <Database className="w-6 h-6 text-green-400" />
              <span>Gerenciar Viagens</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-slate-800 border-red-900/50">
        <CardHeader>
          <CardTitle className="text-red-400 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Zona de Perigo
          </CardTitle>
          <CardDescription className="text-slate-400">
            Ações destrutivas que não podem ser desfeitas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-4">
            Funcionalidades de limpeza e reset do sistema estarão disponíveis aqui.
            Use com extrema cautela.
          </p>
          <Button 
            variant="destructive" 
            disabled
            className="opacity-50 cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar Dados de Teste (Em breve)
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
